import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  isSignal,
  signal,
} from '@angular/core';
import { Tree, TreeItem, TreeItemGroup } from '@angular/aria/tree';
import { UiControlDirective } from '../../../directives/ui/ui-control.directive';
import { IUiTreeSettings, TreeSettings, UTILS } from '@aksia/infrastructure';
import { NgTemplateOutlet } from '@angular/common';
import { IconComponent } from '../../presentation/icon/icon.component';

type TreeNode = {
  name: string;
  value: string;
  icon?: string;
  children?: TreeNode[];
  disabled?: boolean;
  expanded?: boolean;
};

@Component({
  selector: 'tree',
  templateUrl: './tree.component.html',
  imports: [Tree, TreeItem, TreeItemGroup, IconComponent, NgTemplateOutlet],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TreeComponent<T = unknown> extends UiControlDirective {
  protected nodesFromSettings = signal<Array<unknown>>([]);

  override settings = input(new TreeSettings(), {
    transform: (settings: IUiTreeSettings<T>) => this.initSettings(settings),
  });

  protected selected = signal<Array<T>>([]);

  protected override initSettings(settings: IUiTreeSettings<T>) {
    let treeSettings = UTILS.OBJECT.assign(new TreeSettings(), settings);

    super.initSettings(settings);

    if (isSignal(treeSettings.nodes)) {
      this.nodesFromSettings = treeSettings.nodes;
    } else if (typeof treeSettings.nodes === 'function') {
      this.nodesFromSettings.set(treeSettings.nodes());
    } else if (Array.isArray(treeSettings.nodes)) {
      this.nodesFromSettings.set(treeSettings.nodes);
    }

    this.nodes = this._computeNodes(treeSettings);

    return treeSettings;
  }

  protected nodes: Array<TreeNode> = [];

  //Read nodesFromSettings and convert to tree structure, using nodeNameKey and nodeChildrenKeys
  private _computeNodes(settings: IUiTreeSettings<T>): Array<TreeNode> {
    const nameKey = settings.nodeNameKey || 'name';
    const valueKey = settings.nodeValueKey;
    const iconKey = settings.nodeIconKey;
    const childrenKeys = settings.nodeChildrenKeys || ['children'];
    const rawNodes = this.nodesFromSettings();

    const buildTree = (items: any[]): TreeNode[] => {
      return items.map((item) => {
        const node: TreeNode = {
          name: item[nameKey],
          value: valueKey ? item[valueKey] : item,
          icon: iconKey ? item[iconKey] : undefined,
        };
        for (const key of childrenKeys) {
          if (item[key] && Array.isArray(item[key])) {
            node.children = buildTree(item[key]);
            break; // Only consider the first matching children key
          }
        }
        return node;
      });
    };

    console.log('Computed tree nodes:', buildTree(rawNodes));
    return buildTree(rawNodes);
  }
}
